'use client';

import Link from 'next/link';
import Image from 'next/image';
import { UserButton, SignedIn, SignedOut, SignInButton } from '@clerk/nextjs';
import QuotationCartIndicator from './header/QuotationCartIndicator';
import { memo, useMemo, Suspense, useEffect, useCallback, useState, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { Home, ShoppingBag, Package, Building2, Phone, User2, ShieldCheck, ChevronDown, BookOpen, Store, Users } from 'lucide-react';
import { useUser } from '../../hooks/useUser';
import { UserRole } from '@/lib/roles-client';

// ProfileIconLink component to show profile as an icon
const ProfileIconLink = memo(function ProfileIconLink() {
  const pathname = usePathname();
  
  // Simple check if on profile page
  const isActivePath = useMemo(() => {
    return pathname?.startsWith('/perfil');
  }, [pathname]);
  
  return (
    <Link
      href="/perfil"
      className={`relative flex items-center ${isActivePath ? 'text-brand-primary' : 'text-gray-600 hover:text-brand-primary'} transition-colors`}
      aria-label="Mi Perfil"
    >
      <User2 className="h-6 w-6" />
      {isActivePath && (
        <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 bg-brand-primary rounded-full"></span>
      )}
    </Link>
  );
});

// AdminIconLink component to handle admin-only rendering as an icon
const AdminIconLink = memo(function AdminIconLink() {
  const { user, isLoading, isAdmin, refreshUser } = useUser();
  const pathname = usePathname();
  
  // Force refresh when component mounts to get latest role data
  useEffect(() => {
    refreshUser();
  }, [refreshUser]);
  
  // Safety check - hide if user doesn't have admin/owner role
  if (user && (user.role !== UserRole.ADMINISTRADOR && user.role !== UserRole.TITULAR)) {
    return null;
  }
  
  // Hide completely while loading for non-admins (security)
  if (isLoading) {  
    return null;
  }
  
  // Only show the link if user is admin or owner
  if (!isAdmin) {
    return null;
  }
  
  // Simple check if on admin page
  const isActivePath = pathname?.startsWith('/admin');
  
  return (
    <Link
      href="/admin"
      className={`relative flex items-center ${isActivePath ? 'text-brand-primary' : 'text-gray-600 hover:text-brand-primary'} transition-colors`}
      aria-label="Panel de administrador"
    >
      <ShieldCheck className="h-6 w-6" />
      {isActivePath && (
        <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 bg-brand-primary rounded-full"></span>
      )}
    </Link>
  );
});

// RevendedorIconLink component to handle revendedor-only rendering as an icon
const RevendedorIconLink = memo(function RevendedorIconLink() {
  const { user, isLoading, refreshUser } = useUser();
  const pathname = usePathname();
  
  // Force refresh when component mounts to get latest role data
  useEffect(() => {
    refreshUser();
  }, [refreshUser]);
  
  // Safety check - hide if user doesn't have revendedor role
  if (user && user.role !== UserRole.REVENDEDOR) {
    return null;
  }
  
  // Hide completely while loading for non-revendedores (security)
  if (isLoading) {  
    return null;
  }
  
  // Only show the link if user is revendedor
  if (user?.role !== UserRole.REVENDEDOR) {
    return null;
  }
  
  // Simple check if on revendedores page
  const isActivePath = pathname?.startsWith('/revendedores');
  
  return (
    <Link
      href="/revendedores"
      className={`relative flex items-center ${isActivePath ? 'text-brand-primary' : 'text-gray-600 hover:text-brand-primary'} transition-colors`}
      aria-label="Panel de revendedor"
    >
      <Store className="h-6 w-6" />
      {isActivePath && (
        <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 bg-brand-primary rounded-full"></span>
      )}
    </Link>
  );
});

// Original AdminLink component (kept for potential future use)
const AdminLink = memo(function AdminLink() {
  const { user, isLoading, isAdmin, refreshUser } = useUser();
  const pathname = usePathname();
  
  // Force refresh when component mounts to get latest role data
  useEffect(() => {
    refreshUser();
  }, [refreshUser]);
  
  // Safety check - hide if user doesn't have admin/owner role
  if (user && (user.role !== UserRole.ADMINISTRADOR && user.role !== UserRole.TITULAR)) {
    return null;
  }
  
  // Hide completely while loading for non-admins (security)
  if (isLoading) {  
    return null;
  }
  
  // Only show the link if user is admin or owner
  if (!isAdmin) {
    return null;
  }
  
  // Simple check if on admin page
  const isActivePath = pathname?.startsWith('/admin');
  
  return (
    <li>
      <Link
        href="/admin"
        className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
          isActivePath
            ? 'bg-brand-primary text-white'
            : 'text-gray-800 hover:bg-gray-100 hover:text-brand-primary'
        }`}
        aria-current={isActivePath ? 'page' : undefined}
      >
        <ShieldCheck className="h-4 w-4 mr-1.5" aria-hidden="true" />
        Panel de administrador
      </Link>
    </li>
  );
});

// Add a UserDataRefresher component that will be used in the root layout
export const UserDataRefresher = memo(function UserDataRefresher() {
  const { refreshUser } = useUser();
  
  // Force refresh when mounted to ensure we have latest user data
  useEffect(() => {
    // Clear localStorage caches to force fresh data
    if (typeof window !== 'undefined') {
      try {
        // Remove cached user data to force a fresh fetch
        localStorage.removeItem('droquimar-user-data');
      } catch (e) {
        // Silent error handling
      }
    }
    
    // Refresh user data from server
    refreshUser();
    
    // Set up interval to refresh every minute (for role changes)
    const interval = setInterval(() => {
      refreshUser();
    }, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, [refreshUser]);
  
  return null; // This component doesn't render anything
});

// HeaderContent component that uses usePathname
const HeaderContent = memo(function HeaderContent() {
  const pathname = usePathname();
  const [isVerySmallScreen, setIsVerySmallScreen] = useState(false);
  const [isMediumScreen, setIsMediumScreen] = useState(false);
  const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);
  const productsDropdownRef = useRef<HTMLLIElement>(null);
  
  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsVerySmallScreen(window.innerWidth < 375);
      // Medium screen check (between md and lg breakpoints)
      setIsMediumScreen(window.innerWidth >= 768 && window.innerWidth < 1024);
    };
    
    // Initial check
    checkScreenSize();
    
    // Check on resize
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (productsDropdownRef.current && !productsDropdownRef.current.contains(event.target as Node)) {
        setIsProductsDropdownOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Helper to check if a path is active
  const isActivePath = useMemo(() => {
    // Extract the path without locale prefix
    const currentPath = pathname || '/';
    
    // Return a function to check if a given path matches the current one
    return (path: string) => {
      return currentPath === path || currentPath === `${path}/` || currentPath.startsWith(`${path}/`);
    };
  }, [pathname]);
  
  // Menu items with icons for better UX
  const menuItems = useMemo(() => [
    {
      path: '/',
      label: 'Inicio',
      icon: Home
    },
    {
      path: '/empresa',
      label: 'Empresa',
      icon: Building2
    },
    {
      path: '/blog',
      label: 'Blog',
      icon: BookOpen
    },
    {
      path: '/contacto',
      label: 'Contacto',
      icon: Phone
    }
  ], []);
  
  // Products dropdown menu items
  // Note: Online store ("/productos") intentionally omitted — the storefront is
  // hidden until further notice. Restore the entry here when re-enabling.
  const productsMenuItems = useMemo(() => [
    {
      path: '/cotizar',
      label: 'Cotizar',
      icon: ShoppingBag
    },
    {
      path: '/revendedores',
      label: 'Revendedores',
      icon: Users
    }
  ], []);
  
  // Check if any product path is active
  const isProductsActive = useMemo(() => {
    return productsMenuItems.some(item => isActivePath(item.path));
  }, [productsMenuItems, isActivePath]);

  return (
    <header className="sticky top-0 bg-white shadow-md z-50">
      <div className="container mx-auto px-1 sm:px-2 md:px-4 py-2 sm:py-3 flex flex-col md:flex-row md:justify-between md:items-center">
        <div className="flex justify-between items-center w-full md:w-auto">
          <Link href="/" className="flex-shrink-0 flex items-center mr-2">
            <Image 
              src="/logo.svg"
              alt="Droquimar Logo"
              width={isVerySmallScreen ? 32 : 40}
              height={isVerySmallScreen ? 32 : 40}
              className="mr-1 sm:mr-2"
              priority
            />
            <span className={`${isVerySmallScreen ? 'text-base' : 'text-lg sm:text-2xl'} font-bold text-[#ec1c24] hover:text-[#d01920] transition-colors truncate max-w-[120px] sm:max-w-none`}>
              Droquimar
            </span>
          </Link>
          
          <div className="flex items-center gap-1 xs:gap-2 sm:gap-3 md:gap-4 md:hidden ml-auto">
            <QuotationCartIndicator />
            {/* CartIcon intentionally hidden — online store disabled. */}
            <div>
              <SignedIn>
                <UserButton afterSignOutUrl="/" />
              </SignedIn>
              <SignedOut>
                <SignInButton mode="modal">
                  <button className="btn-primary text-sm font-medium whitespace-nowrap">
                    {isVerySmallScreen ? 'Login' : 'Iniciar sesión'}
                  </button>
                </SignInButton>
              </SignedOut>
            </div>
          </div>
        </div>
        
        <div className="hidden md:flex md:flex-row w-full md:w-auto justify-between">
          {/* Navigation Menu integrated into header - desktop only */}
          <nav className="py-2 w-full md:mr-8">
            <ul className="flex flex-wrap justify-center md:justify-start space-x-1 md:space-x-3 lg:space-x-4">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = isActivePath(item.path);
                
                return (
                  <li key={item.path}>
                    <Link
                      href={item.path}
                      className={`flex items-center ${isMediumScreen ? 'px-2' : 'px-3'} py-1.5 rounded-md text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-brand-primary text-white'
                          : 'text-gray-800 hover:bg-gray-100 hover:text-brand-primary'
                      }`}
                      aria-current={isActive ? 'page' : undefined}
                      title={item.label}
                    >
                      <Icon className="h-4 w-4 md:mr-0 lg:mr-1.5" aria-hidden="true" />
                      <span className="hidden lg:inline">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
              
              {/* Products Dropdown Menu */}
              <li className="relative" ref={productsDropdownRef}>
                <button
                  onClick={() => setIsProductsDropdownOpen(!isProductsDropdownOpen)}
                  className={`flex items-center dropdown-button ${isMediumScreen ? 'px-2' : 'px-3'} py-1.5 rounded-md text-sm font-medium transition-colors ${
                    isProductsActive
                      ? 'bg-brand-primary text-white'
                      : 'text-gray-800 hover:bg-gray-100 hover:text-brand-primary'
                  }`}
                  title="Productos"
                >
                  <Package className="h-4 w-4 md:mr-0 lg:mr-1.5" aria-hidden="true" />
                  <span className="hidden lg:inline">Productos</span>
                  <ChevronDown className={`h-4 w-4 dropdown-chevron transform transition-transform ${isProductsDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isProductsDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 bg-white shadow-lg rounded-md w-40 py-1 z-50">
                    {productsMenuItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = isActivePath(item.path);
                      
                      return (
                        <Link
                          key={item.path}
                          href={item.path}
                          className={`flex items-center px-3 py-2 text-sm transition-colors ${
                            isActive
                              ? 'bg-brand-light text-brand-primary font-medium'
                              : 'text-gray-800 hover:bg-gray-50 hover:text-brand-primary'
                          }`}
                          title={item.label}
                        >
                          <Icon className="h-4 w-4 mr-1.5" aria-hidden="true" />
                          <span>{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </li>
            </ul>
          </nav>
          
          <div className="hidden md:flex items-center space-x-4">
            <QuotationCartIndicator />
            {/* CartIcon intentionally hidden — online store disabled. */}
            <SignedIn>
              <AdminIconLink />
              <RevendedorIconLink />
              <ProfileIconLink />
            </SignedIn>
            <div>
              <SignedIn>
                <UserButton afterSignOutUrl="/" />
              </SignedIn>
              <SignedOut>
                <SignInButton mode="modal">
                  <button className="btn-primary text-sm font-medium whitespace-nowrap">
                    Iniciar sesión
                  </button>
                </SignInButton>
              </SignedOut>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
});

// Fallback component for Suspense
const HeaderFallback = () => (
  <header className="sticky top-0 bg-white shadow-md z-50">
    <div className="container mx-auto px-1 sm:px-2 md:px-4 py-2 sm:py-3 flex flex-col md:flex-row md:justify-between md:items-center">
      <div className="flex justify-between items-center w-full md:w-auto">
        <Link href="/" className="flex-shrink-0 flex items-center mr-2">
          <Image 
            src="/logo.svg"
            alt="Droquimar Logo"
            width={35}
            height={35}
            className="mr-1 sm:mr-2"
            priority
          />
          <span className="text-base sm:text-xl md:text-2xl font-bold text-brand-primary hover:text-brand-dark transition-colors truncate max-w-[120px] sm:max-w-none">
            Droquimar
          </span>
        </Link>
      </div>
    </div>
  </header>
);

// Main Header component that wraps HeaderContent in Suspense
const Header = memo(function Header() {
  return (
    <Suspense fallback={<HeaderFallback />}>
      <HeaderContent />
    </Suspense>
  );
});

export default Header;